import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ImageCropper from "../../../components/ImageCropper";
import { baseFetch } from "../../../helpers/apiClient";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_ADDITIONAL_PHOTOS,
  MAX_IMAGE_BYTES,
} from "../../../helpers/imageUpload";
import { uploadProfilePhoto } from "../../../helpers/uploadProfilePhoto";

/**
 * Manage the extra portraits collected during onboarding (ECH-373).
 *
 * Onboarding was the only thing that ever wrote these, so a member could upload
 * three during signup and never change them. Shared by the individual profile
 * editor rather than copied into each role-specific screen.
 *
 * ## Why the upload is three steps
 *
 * `POST /v1/app/profile/additionalPhotos` verifies that every url is an Echo
 * upload owned by the caller. Only the presigned flow produces one: it writes to
 * `profile-pics/` and tags the object with the uploader's id. The one-shot
 * `/v1/web/upload/image` writes untagged objects to `web/` and is rejected, even
 * though it succeeds and returns a perfectly good-looking url.
 *
 * ## Why the whole set is sent every time
 *
 * The endpoint is a replace, and position comes from array order. Sending a
 * single photo would delete the others.
 */
const AdditionalPhotosEditor = ({ photos = [], onChange }) => {
  const mountedRef = useRef(true);
  const operationRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cropSelection, setCropSelection] = useState(null);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      operationRef.current?.abort();
    };
  }, []);

  useEffect(
    () => () => {
      if (cropSelection?.source) URL.revokeObjectURL(cropSelection.source);
    },
    [cropSelection],
  );

  const persistPhotos = async (next, signal) => {
    await baseFetch({
      url: "/v1/app/profile/additionalPhotos",
      method: "POST",
      body: { photos: next },
      signal,
    });
    if (mountedRef.current) onChange(next);
  };

  const save = async (next) => {
    const controller = new AbortController();
    operationRef.current = controller;
    setBusy(true);
    setError("");
    try {
      await persistPhotos(next, controller.signal);
    } catch (err) {
      if (mountedRef.current) {
        setError(
          err?.message ?? "Those photos could not be saved. Please try again.",
        );
      }
    } finally {
      if (mountedRef.current && operationRef.current === controller) {
        operationRef.current = null;
        setBusy(false);
      }
    }
  };

  const selectPhoto = (file) => {
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError("Choose a JPG, PNG, GIF or WebP image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("That image is larger than 10 MB.");
      return;
    }

    setError("");
    setCropSelection({ file, source: URL.createObjectURL(file) });
  };

  const upload = async (croppedFile) => {
    if (!cropSelection || !croppedFile) return;

    const file = new File([croppedFile], cropSelection.file.name, {
      type: croppedFile.type || cropSelection.file.type,
      lastModified: cropSelection.file.lastModified,
    });
    if (file.size > MAX_IMAGE_BYTES) {
      setError("That image is larger than 10 MB.");
      return;
    }
    setCropSelection(null);
    const controller = new AbortController();
    operationRef.current = controller;
    setBusy(true);
    setError("");
    try {
      const url = await uploadProfilePhoto(file, {
        signal: controller.signal,
      });
      await persistPhotos([...photos, url], controller.signal);
    } catch (err) {
      if (mountedRef.current) {
        setError(
          err?.message ?? "That photo could not be uploaded. Please try again.",
        );
        setBusy(false);
      }
    } finally {
      if (mountedRef.current && operationRef.current === controller) {
        operationRef.current = null;
        setBusy(false);
      }
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
        Additional photos
      </Typography>
      <Typography sx={{ mb: 1.5, fontSize: "0.8125rem", color: "#6B7280" }}>
        Up to {MAX_ADDITIONAL_PHOTOS} more photos alongside your profile
        picture.
      </Typography>

      {error && (
        <Typography
          role="alert"
          sx={{ mb: 1.5, fontSize: "0.8125rem", color: "#B3312C" }}
        >
          {error}
        </Typography>
      )}

      <Stack direction="row" gap={1.5} flexWrap="wrap">
        {photos.map((url, index) => (
          <Box
            key={url}
            sx={{ position: "relative", width: 96, aspectRatio: "3 / 4" }}
          >
            <img
              src={url}
              alt={`Additional photo ${index + 1}`}
              width="100%"
              height="100%"
              style={{ objectFit: "cover", borderRadius: 8 }}
            />
            <IconButton
              aria-label={`Remove additional photo ${index + 1}`}
              disabled={busy}
              onClick={() => save(photos.filter((_, i) => i !== index))}
              size="small"
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                background: "#fff",
                boxShadow: 1,
                "&:hover": { background: "#fff" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        {photos.length < MAX_ADDITIONAL_PHOTOS && (
          <Box
            component="label"
            aria-label="Add an additional photo"
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.currentTarget.querySelector("input")?.click();
              }
            }}
            sx={{
              width: 96,
              aspectRatio: "3 / 4",
              border: "1px dashed #C7CDD4",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            <AddPhotoAlternateOutlinedIcon sx={{ color: "#6B7280" }} />
            <input
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(",")}
              disabled={busy}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Cleared so choosing the same file twice still fires a change.
                e.target.value = "";
                selectPhoto(file);
              }}
            />
          </Box>
        )}
      </Stack>

      {cropSelection && (
        <ImageCropper
          open
          image={cropSelection.source}
          aspectRatio={3 / 4}
          onCrop={upload}
          onCancel={() => setCropSelection(null)}
          loading={busy}
        />
      )}
    </Box>
  );
};

export default AdditionalPhotosEditor;
